/**
 * Centralized toast message constants for consistent messaging across the application.
 *
 * Using constants ensures:
 * - Consistent messaging to users
 * - Easier to update messages globally
 * - Better i18n support in the future
 * - Type-safe message templates
 */

export const TOAST_MESSAGES = {
  // Settings
  SETTINGS_SAVED: 'Settings saved successfully',
  SETTINGS_SAVE_FAILED: (error: string) => `Failed to save settings: ${error}`,

  // Sync operations
  SYNC_SUCCESS: (count: number) => `Synced ${count} products`,
  SYNC_FAILED: (error: string) => `Sync failed: ${error}`,
  SYNC_STARTED: 'Sync started',
  SYNC_IN_PROGRESS: 'Processing products in background...',
  SYNC_COMPLETE: 'Sync complete',
  SYNC_CANCELLED: 'Sync cancelled',

  // Product operations
  PRODUCT_CREATED: 'Product created successfully',
  PRODUCT_UPDATED: 'Product updated successfully',
  PRODUCT_DELETED: 'Product deleted successfully',
  PRODUCT_DUPLICATED: 'Product duplicated successfully',

  // Bulk operations
  BULK_OPERATION_STARTED: 'Bulk operation started',
  BULK_OPERATION_COMPLETE: (count: number) => `Updated ${count} products`,
  BULK_OPERATION_FAILED: 'Bulk operation failed',

  // Store operations
  STORE_CONNECTED: 'Store connected successfully',
  STORE_DISCONNECTED: 'Store disconnected',
  STORE_CONNECTION_FAILED: (error: string) => `Connection failed: ${error}`,

  // Categories
  CATEGORY_CREATED: 'Category created successfully',
  CATEGORY_UPDATED: 'Category updated successfully',
  CATEGORY_DELETED: 'Category deleted successfully',

  // Media
  MEDIA_UPLOADED: 'Media uploaded successfully',
  MEDIA_DELETED: 'Media deleted successfully',

  // Reviews
  REVIEW_APPROVED: 'Review approved',
  REVIEW_REJECTED: 'Review rejected',
  REVIEW_DELETED: 'Review deleted',

  // Orders
  ORDER_STATUS_UPDATED: 'Order status updated',
  ORDER_NOTE_ADDED: 'Note added to order',

  // Coupons
  COUPON_CREATED: 'Coupon created successfully',
  COUPON_UPDATED: 'Coupon updated successfully',
  COUPON_DELETED: 'Coupon deleted successfully',

  // SEO
  SEO_OPTIMIZED: (count: number) => `Optimized ${count} products`,
  SEO_OPTIMIZATION_FAILED: 'Optimization failed',

  // Generic errors
  ERROR_NO_STORE: 'No store selected',
  ERROR_NOT_AUTHENTICATED: 'Not authenticated',
  ERROR_GENERIC: (error: string) => `Error: ${error}`,
} as const;
